// MFA (Multi-Factor Authentication) constants

/**
 * MFA session cache policy (MFA Part IV — cached post-MFA token TTL preference).
 * Persisted via `PUT /users/:email` as `mfaSessionCachePolicy` (`always` | `8h` | `12h`).
 * At MFA completion time the client does not send this in Cognito clientMetadata (no browser
 * cache); the backend should apply TTL from hub_users (or an equivalent trusted store) by user.
 */
export const MFA_SESSION_CACHE_POLICY_FIELD = 'mfaSessionCachePolicy' as const;

export const MFA_SESSION_CACHE_POLICY_ALWAYS = 'always' as const;
export const MFA_SESSION_CACHE_POLICY_8H = '8h' as const;
export const MFA_SESSION_CACHE_POLICY_12H = '12h' as const;

export const VALID_MFA_SESSION_CACHE_POLICIES = [
  MFA_SESSION_CACHE_POLICY_ALWAYS,
  MFA_SESSION_CACHE_POLICY_8H,
  MFA_SESSION_CACHE_POLICY_12H,
] as const;

export type MfaSessionCachePolicy =
  (typeof VALID_MFA_SESSION_CACHE_POLICIES)[number];

export function normalizeMfaSessionCachePolicy(
  value: string | null | undefined
): MfaSessionCachePolicy {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (v === MFA_SESSION_CACHE_POLICY_8H) {
    return MFA_SESSION_CACHE_POLICY_8H;
  }
  if (v === MFA_SESSION_CACHE_POLICY_12H) {
    return MFA_SESSION_CACHE_POLICY_12H;
  }
  return MFA_SESSION_CACHE_POLICY_ALWAYS;
}

// MFA method types - standardized values used throughout the application
export const MFA_METHOD_EMAIL = 'email' as const;
export const MFA_METHOD_SMS = 'sms' as const;
export const MFA_METHOD_TOTP = 'totp' as const;

// Valid MFA methods array for validation
export const VALID_MFA_METHODS = [
  MFA_METHOD_EMAIL,
  MFA_METHOD_SMS,
  MFA_METHOD_TOTP,
] as const;

// Cognito custom attribute names
export const COGNITO_ATTR_PREFERRED_MFA_METHOD = 'custom:preferredMfaMethod';
export const COGNITO_ATTR_PHONE_NUMBER = 'phone_number';
export const COGNITO_ATTR_PHONE_NUMBER_VERIFIED = 'phone_number_verified';

// Verification code settings
// OTP codes are 6 digits (100000 to 999999)
export const OTP_CODE_MIN = 100000;
export const OTP_CODE_MAX = 999999;

// Verification code expiration time in milliseconds (5 minutes)
export const VERIFICATION_CODE_EXPIRATION_MS = 5 * 60 * 1000;

// Rate limiting settings for phone verification
// Maximum number of verification code requests per user per time window
export const MAX_VERIFICATION_REQUESTS_PER_WINDOW = 5;
// Time window for rate limiting in milliseconds (15 minutes)
export const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000;

// E.164 phone number format regex
// Matches international phone numbers: + followed by 1-15 digits
// First digit after + must be 1-9 (not 0)
export const E164_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

// TOTP (Time-based One-Time Password) constants
// Issuer name for TOTP QR codes
export const TOTP_ISSUER = 'Cornerstone Upstream';
// Pending TOTP secret expiration time in seconds (10 minutes)
export const PENDING_TOTP_TTL_SECONDS = 600;

// Type for MFA method values
export type MfaMethod =
  | typeof MFA_METHOD_EMAIL
  | typeof MFA_METHOD_SMS
  | typeof MFA_METHOD_TOTP;
